import type {
  TFilterStoring,
  TFilterStoringOptions,
  TFilterStoringRange,
} from '../composables/catalogFilter'
import type { TFilterCode } from '../store/catalog'
import type {
  TFilterItem,
  TFilterItemMultiselect,
  TFilterItemRange,
} from './types'
import type { CatalogDataProviderType } from '~/layers/catalog/composables/catalogDataProvider'

import { TFilterStoringType } from '../composables/catalogFilter'
import { useCatalogStore } from '../store/catalog'
import { TFilterItemType } from './types'

type TCatalogUrlStoreFilter = {
  code: TFilterCode
  payload: TFilterStoring
}

type TCatalogUrlBuildState = {
  filters: TCatalogUrlStoreFilter[]
  isSortChanged: boolean
  page: number
  selectedCategories: string[]
  sort: {
    code: string | null
    direction: 'desc' | 'asc' | null
  }
}

type TCatalogUrlBuildOptions = {
  basePath?: string
  page?: number
  useCategoryPath?: boolean
}

export const buildCatalogUrlLocation = (
  filters: TFilterItem[],
  type: CatalogDataProviderType,
  state: TCatalogUrlBuildState,
  options: TCatalogUrlBuildOptions = {},
): { path: string; query: Record<string, string> } => {
  const retrieveBasePath = () => {
    if (options.basePath) {
      return options.basePath.endsWith('/')
        ? options.basePath
        : `${options.basePath}/`
    }

    let basePath = type === 'jewelry' ? '/jewelry/' : '/diamonds/'
    if (state.selectedCategories.length) {
      basePath += `${state.selectedCategories.join('_or_')}/`
    }
    return basePath
  }

  let url = retrieveBasePath()
  const filtersParts = []

  if (options.useCategoryPath === false && state.selectedCategories.length) {
    filtersParts.push(`categories--${state.selectedCategories.join('_or_')}`)
  }

  const sortedStoreFilters = [...state.filters].sort((a, b) => {
    const aSort =
      filters.find(
        (f) => f.code.replace('_rf', '') === a.code.replace('_rf', ''),
      )?.applyingOrder ?? 0
    const bSort =
      filters.find(
        (f) => f.code.replace('_rf', '') === b.code.replace('_rf', ''),
      )?.applyingOrder ?? 0

    return aSort - bSort
  })

  for (const filter of sortedStoreFilters) {
    if (filter.payload.type === TFilterStoringType.Options) {
      const payload = filter.payload as TFilterStoringOptions & TFilterStoring
      if (payload.options.length === 0) {
        continue
      }

      const findFilter = filters.find(
        (f) => f.code === filter.code,
      ) as TFilterItem & TFilterItemMultiselect
      const options = [...payload.options].sort((a, b) => {
        if (findFilter?.type !== TFilterItemType.Multiselect) {
          return a.localeCompare(b)
        }

        const aSort = findFilter.items.findIndex((i) => i.code === a)
        const bSort = findFilter.items.findIndex((i) => i.code === b)

        return aSort - bSort
      })

      filtersParts.push(`${filter.code}--` + options.join('_or_'))
    }

    if (filter.payload.type === TFilterStoringType.Range) {
      const payload = filter.payload as TFilterStoringRange

      if (!payload.range.from && !payload.range.to) {
        continue
      }

      const findFilter = filters.find(
        (f) => f.code === filter.code,
      ) as TFilterItemRange & TFilterItem
      const rangeQuery = [
        payload.range.from || findFilter?.range.from || '',
        payload.range.to || findFilter?.range.to || '',
      ]

      filtersParts.push(`${filter.code}--${rangeQuery.join('_to_')}`)
    }
  }

  if (filtersParts.length) {
    url += 'filter/' + filtersParts.join('/') + '/'
  }

  const page = options.page ?? state.page
  if (page > 1) {
    url += page.toString() + '/'
  }

  const query: Record<string, string> = {}

  if (state.isSortChanged && state.sort.code && state.sort.direction) {
    query.sortBy = state.sort.code
    query.order = state.sort.direction
  }

  return {
    path: url,
    query,
  }
}

export const catalogURL = (
  categoriesCodes: string[] | undefined | null = undefined,
) => {
  if (
    categoriesCodes?.length &&
    categoriesCodes.every((code) => new RegExp(/^\d+$/).test(code))
  ) {
    categoriesCodes = null
  }
  const store = useCatalogStore()

  const extractPageNumber = (url: string): number | null => {
    const regex = /\/(\d+)(?:\/|\?|$)/

    return url.match(regex)?.[1] ? parseInt(url.match(regex)![1], 10) : null
  }

  const getCurrentLocation = () => {
    if (typeof window !== 'undefined') {
      const path = decodeURI(window.location.pathname)
      const segments = path.split('/').filter(Boolean)
      const potentialCategory = segments[1]
      const category =
        potentialCategory &&
        potentialCategory !== 'filter' &&
        !/^\d+$/.test(potentialCategory)
          ? potentialCategory
          : undefined

      return {
        path,
        query: Object.fromEntries(new URLSearchParams(window.location.search)),
        category,
      }
    } else {
      const route = useRoute()
      return {
        path: decodeURI(route.path),
        query: route.query,
        category: route.params.category as string | undefined,
      }
    }
  }

  return {
    parse(options: { parseCategory?: boolean } = {}) {
      const { parseCategory = true } = options
      const { path, query, category } = getCurrentLocation()
      const page = extractPageNumber(path) || 1

      if (parseCategory && category && !/^\d+$/.test(category)) {
        const categories: string[] = []
        for (const code of category.split('_or_')) {
          if (code) {
            categories.push(code)
          }
        }
        store.setSelectedCategories(categories)
      } else if (parseCategory) {
        store.setSelectedCategories([])
      }

      const [, filtersPath] = path.split('/filter/')

      let parts = filtersPath?.split('/') || []
      if (!Array.isArray(parts)) {
        parts = [parts]
      }

      const existingFiltersCodes = new Set(store.getFiltersCodes())
      let seenCategoriesFilter = false
      const seenFiltersCodes = new Set<string>()

      for (const part of parts) {
        if (part.length === 0) {
          continue
        }

        if (!part.includes('--')) {
          continue
        }

        const [filterCode, optionsPart] = part.split('--')

        if (!parseCategory && filterCode === 'categories') {
          store.setSelectedCategories(optionsPart.split('_or_').filter(Boolean))
          seenCategoriesFilter = true
          continue
        }

        seenFiltersCodes.add(filterCode)

        /** Range **/
        if (optionsPart.includes('_to_')) {
          const rangeQuery = optionsPart.split('_to_')
          store.setFilter(filterCode, {
            type: TFilterStoringType.Range,
            range: { from: rangeQuery[0], to: rangeQuery[1] },
          })
          continue
        }

        /** Multiselect **/
        const options = optionsPart.split('_or_')
        store.setFilter(filterCode, {
          type: TFilterStoringType.Options,
          options,
        })

        if (filterCode.includes('_rf')) {
          store.showRuValuesFor.push(filterCode.replace('_rf', ''))
        }
      }

      for (const code of existingFiltersCodes) {
        if (!seenFiltersCodes.has(code)) {
          store.removeFilter(code)
        }
      }

      if (!parseCategory && !seenCategoriesFilter) {
        store.setSelectedCategories([])
      }

      if (query.sortBy && query.order) {
        store.setSort(query.sortBy as string, query.order as 'asc' | 'desc')
      } else {
        store.resetSort()
      }
      if (page) {
        store.setPage(page)
        store.setInitialPage(page)
      }
    },
    apply: async function (
      filters: TFilterItem[],
      type: CatalogDataProviderType,
      options: { basePath?: string; useCategoryPath?: boolean } = {},
    ) {
      await useRouter().push(
        buildCatalogUrlLocation(filters, type, store, options),
      )
    },
  }
}
